import { Component, Input, AfterViewInit, ViewChild, ElementRef, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import Chart from 'chart.js';

@Component({
  selector: 'app-gauge',
  template: `<canvas #gaugeCanvas></canvas>`,
  styles: [':host { display: block; width: 100%; max-width: 220px; }']
})
export class GaugeComponent implements AfterViewInit, OnChanges, OnDestroy {

  @ViewChild('gaugeCanvas', { static: false }) gaugeCanvas!: ElementRef;
  @Input() value: number = 0;
  @Input() type: 'IMC' | 'ICT' | 'ABSI' = 'IMC';

  private gaugeChart: Chart | null = null;
  private currentAngle: number = Math.PI; // ángulo actual de la aguja (empieza a la izquierda)
  private targetAngle: number = Math.PI;
  private animationId: number | null = null;
  private readonly ANIMATION_SPEED = 0.12; // factor de interpolación por frame (más alto = más rápido)

  private getGaugeConfig() {
    if (this.type === 'IMC') {
      return {
        ranges: [
          { min: 10, max: 18.5 },
          { min: 18.5, max: 25 },
          { min: 25, max: 30 },
          { min: 30, max: 45 },
        ],
        colors: ['#ffeb3b', '#43a047', '#ffa726', '#f44336'],
      };
    } else if (this.type === 'ICT') {
      return {
        ranges: [
          { min: 0.2, max: 0.4 },
          { min: 0.4, max: 0.5 },
          { min: 0.5, max: 0.6 },
          { min: 0.6, max: 0.8 },
        ],
        colors: ['#ffeb3b', '#43a047', '#ffa726', '#f44336'],
      };
    } else if (this.type === 'ABSI') {
      return {
        ranges: [
          { min: 0.07, max: 0.081 },
          { min: 0.081, max: 0.083 },
          { min: 0.083, max: 0.085 },
          { min: 0.085, max: 0.095 },
        ],
        colors: ['#43a047', '#ffeb3b', '#ffa726', '#f44336'],
      };
    }
    return {
      ranges: [
        { min: 0, max: 25 },
        { min: 25, max: 50 },
        { min: 50, max: 75 },
        { min: 75, max: 100 },
      ],
      colors: ['#43a047', '#ffeb3b', '#ffa726', '#f44336'],
    };
  }

  private getNeedleAngle(): number {
    const config = this.getGaugeConfig();
    const ranges = config.ranges;
    const numSegments = ranges.length;

    let segmentIndex = numSegments - 1;
    for (let i = 0; i < numSegments; i++) {
      if (this.value < ranges[i].max) {
        segmentIndex = i;
        break;
      }
    }

    const range = ranges[segmentIndex];
    const clamped = Math.max(range.min, Math.min(range.max, this.value));
    const fractionInSegment = (clamped - range.min) / (range.max - range.min);
    const totalFraction = (segmentIndex + fractionInSegment) / numSegments;

    return Math.PI + Math.PI * totalFraction;
  }

  ngAfterViewInit() {
    this.createChart();
    this.targetAngle = this.getNeedleAngle();
    this.startAnimation();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['type'] && this.gaugeChart) {
      // Si cambia el tipo, recrear el chart (colores distintos)
      this.createChart();
    }
    if ((changes['value'] || changes['type']) && this.gaugeCanvas) {
      this.targetAngle = this.getNeedleAngle();
      this.startAnimation();
    }
  }

  ngOnDestroy(): void {
    this.stopAnimation();
    if (this.gaugeChart) {
      this.gaugeChart.destroy();
      this.gaugeChart = null;
    }
  }

  private createChart(): void {
    if (!this.gaugeCanvas) return;
    if (this.gaugeChart) {
      this.gaugeChart.destroy();
      this.gaugeChart = null;
    }

    const config = this.getGaugeConfig();
    const self = this;

    this.gaugeChart = new Chart(this.gaugeCanvas.nativeElement, {
      type: 'doughnut',
      data: {
        datasets: [{
          data: [1, 1, 1, 1],
          backgroundColor: config.colors,
          borderWidth: 0,
        }]
      },
      options: {
        animation: { animateRotate: false, animateScale: false },
        rotation: Math.PI,
        circumference: Math.PI,
        cutout: '92%',
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false },
          datalabels: { display: false }
        },
        hover: { mode: null },
        events: []
      },
      plugins: [{
        afterDraw: (chart) => {
          self.drawNeedle(chart);
        }
      }]
    });
  }

  private drawNeedle(chart: any): void {
    const ctx = chart.ctx;
    const centerX = chart.width / 2;
    const centerY = chart.height - chart.height / 6;
    const radius = chart.width / 2.1;
    const angle = this.currentAngle;

    // Aguja negra fina
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.lineTo(centerX + radius * Math.cos(angle), centerY + radius * Math.sin(angle));
    ctx.strokeStyle = '#222';
    ctx.lineWidth = 2.2;
    ctx.shadowColor = 'rgba(0,0,0,0.15)';
    ctx.shadowBlur = 4;
    ctx.stroke();
    ctx.restore();

    // Base redonda
    ctx.save();
    ctx.beginPath();
    ctx.arc(centerX, centerY, 8, 0, 2 * Math.PI);
    ctx.fillStyle = '#222';
    ctx.shadowColor = 'rgba(0,0,0,0.18)';
    ctx.shadowBlur = 6;
    ctx.fill();
    ctx.restore();

    // Punta triangular
    ctx.save();
    const tipX = centerX + radius * Math.cos(angle);
    const tipY = centerY + radius * Math.sin(angle);
    const triangleSize = 12;
    const baseAngle1 = angle - 0.13;
    const baseAngle2 = angle + 0.13;
    ctx.beginPath();
    ctx.moveTo(tipX, tipY);
    ctx.lineTo(
      tipX - triangleSize * Math.cos(baseAngle1),
      tipY - triangleSize * Math.sin(baseAngle1)
    );
    ctx.lineTo(
      tipX - triangleSize * Math.cos(baseAngle2),
      tipY - triangleSize * Math.sin(baseAngle2)
    );
    ctx.closePath();
    ctx.fillStyle = '#222';
    ctx.shadowColor = 'rgba(0,0,0,0.18)';
    ctx.shadowBlur = 6;
    ctx.fill();
    ctx.restore();
  }

  private startAnimation(): void {
    if (this.animationId != null) return; // ya está corriendo
    this.animate();
  }

  private stopAnimation(): void {
    if (this.animationId != null) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  private animate = (): void => {
    const diff = this.targetAngle - this.currentAngle;

    if (Math.abs(diff) < 0.002) {
      // Llegó al destino
      this.currentAngle = this.targetAngle;
      if (this.gaugeChart) this.gaugeChart.update(0);
      this.animationId = null;
      return;
    }

    // Interpolación lineal suave (ease-out)
    this.currentAngle += diff * this.ANIMATION_SPEED;
    if (this.gaugeChart) this.gaugeChart.update(0);
    this.animationId = requestAnimationFrame(this.animate);
  }
}
